import React from "react";

import { Box, Button, CircularProgress, Grid, IconButton, Stack, Typography } from "@mui/material";
import {
    CloseOutlined as CloseOutlinedIcon,
    SyncProblem as SyncProblemIcon
} from "@mui/icons-material";

import { DataLayout, StatusAvatar, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";
import { useCause } from "../hooks/useCause";
import { ScroreCard } from "./ScroreCard";
import { useFetchRisk } from "../mutation";

import { useCapabilities } from "../../../hooks/useCapabilities";
import { useMitigation } from "../hooks/useMitigation";
import { useAssessment } from "../hooks/useAssessment";

export const RiskHeader = ({ riskId, handleClose }: { riskId: string | null, handleClose: () => void }) => {

    const { riskData, isFetching } = useFetchRisk({ riskId: riskId });

    if (isFetching || !riskData) {
        return (
            <Box height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ padding: 2, display: 'flex', justifyContent: 'start', bgcolor: 'action.hover', alignItems: 'center', gap: 2, width: '100%' }}>
            <Box>
                <StatusAvatar bgcolor="warning.main">
                    <SyncProblemIcon />
                </StatusAvatar>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: .5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.primary">{riskData.project_title}</Typography>
                    <StatusChip variant='outlined' color="red" label={String(riskData.risk_status_text)} />
                </Box>
                <Typography variant="caption" color="text.primary">{riskData.risk_description}</Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ ml: 'auto' }}>
                <CloseOutlinedIcon sx={{ color: 'action.active' }} />
            </IconButton>
        </Box>
    );

};

export const RiskDetailsOverview: React.FC<{ riskId: string | null, onEdit: () => void; refetch: () => void }> = ({ riskId, onEdit, refetch }) => {

    const { isAllowedToRaiseRisk, isAllowedEditRisk } = useCapabilities();

    const { riskData, isRiskPending } = useFetchRisk({ riskId: riskId });

    const { causeData, isCauseFetching } = useCause({ riskId: riskId, refetch: refetch });
    const { assessmentData, isAssessmentFetching } = useAssessment({ riskId: riskId, refetch: refetch });
    const { mitigationData, isMitigationFetching } = useMitigation({ riskId: riskId, refetch: refetch });

    if (isCauseFetching || isAssessmentFetching || isMitigationFetching || isRiskPending) {
        return (
            <Box height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'} overflow={'hidden'}>
            <Box flex={1} p={2} sx={{ overflowY: 'scroll' }}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                    <DataLayout title="Risk Details" bgVariant="filled">
                        <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                            {
                                [
                                    { label: 'Title', value: riskData?.risk_title },
                                    { label: 'Risk Description', value: riskData?.risk_description },
                                    { label: 'Category', value: riskData?.risk_category_text },
                                    { label: 'Raised By', value: riskData?.raised_by },
                                    { label: 'Assigned to', value: riskData?.risk_owner  ?? "-" },
                                    { label: 'Raised at', value: formatDate(riskData?.raised_at) },
                                ].map((value, index) => (
                                    <Grid container key={index}>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.secondary">{value.label}</Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.primary">{value.value}</Typography>
                                        </Grid>
                                    </Grid>
                                ))
                            }
                        </Box>
                    </DataLayout>
                    <DataLayout title="Current Assessment" bgVariant="filled">
                        <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2} py={2}>
                            <Typography variant="caption" color="text.secondary">Impact</Typography>
                            <StatusChip label={riskData?.assessment_impact} color="amber" />
                            <Typography variant="caption" color="text.secondary">Probability</Typography>
                            <StatusChip label={riskData?.assessment_probability} color="red" />
                            <Typography variant="caption" color="text.secondary">Score</Typography>
                            <ScroreCard score={riskData?.current_assessment} />
                        </Box>
                    </DataLayout>
                    <DataLayout title="Residual Assessment" bgVariant="filled">
                        <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2} py={2}>
                            <Typography variant="caption" color="text.secondary">Impact</Typography>
                            <StatusChip label={riskData?.mitigation_impact} color="amber" />
                            <Typography variant="caption" color="text.secondary">Probability</Typography>
                            <StatusChip label={riskData?.mitigation_probability} color="red" />
                            <Typography variant="caption" color="text.secondary">Score</Typography>
                            <ScroreCard score={riskData?.residual_score} />
                        </Box>
                    </DataLayout>
                    <DataLayout title="Causes" bgVariant="filled">
                        {
                            causeData?.length === 0 ?
                                <Box p={2}>
                                    <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No causes found.</Typography>
                                </Box> :
                                causeData?.map((cause: { title: string; description: string; status: string; }, index: React.Key | null | undefined) => (
                                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} p={2} borderColor={'divider'} borderRadius={1} sx={{ borderWidth: '1px', borderStyle: 'solid' }} m={2} key={index}>
                                        <Box display={'flex'} flexDirection={'column'} alignItems={'start'}>
                                            <Typography variant="subtitle2" color="text.primary">{cause.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{cause.description}</Typography>
                                        </Box>
                                        <StatusChip label={cause.status} color={cause.status === 'Open' ? 'amber' : 'default'} />
                                    </Box>
                                ))
                        }
                    </DataLayout>
                    <DataLayout title="Impact Assessment" bgVariant="filled">
                        {
                            assessmentData?.length === 0 ?
                                <Box p={2}>
                                    <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No assessments found.</Typography>
                                </Box> :
                                assessmentData?.map((assessment: { title: string; description: string; status: string; impact: string; probability: string; }, index: React.Key | null | undefined) => (
                                    <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} justifyContent={'space-between'} p={2} borderColor={'divider'} borderRadius={1} sx={{ borderWidth: '1px', borderStyle: 'solid' }} m={2} key={index}>
                                        <Box display={'flex'} flexDirection={'column'} alignItems={'start'}>
                                            <Typography variant="subtitle2" color="text.primary">{assessment.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{assessment.description}</Typography>
                                        </Box>
                                        <StatusChip label={assessment.status} color={assessment.status === 'Open' ? 'amber' : 'default'} />
                                        <Box display={'flex'} alignItems={'center'} width={'100%'} mt={2} gap={2}>
                                            <Typography variant="caption" color="text.secondary">Impact</Typography>
                                            <StatusChip label={assessment.impact} color="amber" />
                                            <Typography variant="caption" color="text.secondary">Probability</Typography>
                                            <StatusChip label={assessment.probability} color="red" />
                                        </Box>
                                    </Box>
                                ))
                        }
                    </DataLayout>
                    <DataLayout title="Mitigations / Treatment" bgVariant="filled">
                        {
                            mitigationData?.length === 0 ?
                                <Box p={2}>
                                    <Typography variant="body2" color="text.secondary">No mitigations found.</Typography>
                                </Box>
                                :
                                mitigationData?.map((mitigation: { title: string; description: string; status: string; residual_impact: string; residual_probability: string; }, index: React.Key | null | undefined) => (
                                    <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} justifyContent={'space-between'} p={2} borderColor={'divider'} borderRadius={1} sx={{ borderWidth: '1px', borderStyle: 'solid' }} m={2} key={index}>
                                        <Box display={'flex'} flexDirection={'column'} alignItems={'start'}>
                                            <Typography variant="subtitle2" color="text.primary">{mitigation.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{mitigation.description}</Typography>
                                        </Box>
                                        <Box display={'flex'} alignItems={'center'} width={'100%'} mt={2} gap={2}>
                                            <Typography variant="caption" color="text.secondary">Impact</Typography>
                                            <StatusChip label={mitigation.residual_impact} color="amber" />
                                            <Typography variant="caption" color="text.secondary">Probability</Typography>
                                            <StatusChip label={mitigation.residual_probability} color="red" />
                                        </Box>
                                    </Box>
                                ))
                        }
                    </DataLayout>
                </Box>
            </Box>
            {
                isAllowedEditRisk && (
                    <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                        <Button onClick={onEdit} variant="outlined">Edit risk</Button>
                    </Stack>
                )
            }
        </Box>
    )
}